/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import orderBy from 'lodash/orderBy'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import Table from 'components/Table'
import { Tooltip } from 'components/Tooltip'
import TableButtons from 'components/TableButtons/TableButtons'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { BULK_UPDATE_AI_TEST, FIND_ALL_AI_TEST, UPDATE_AI_TEST } from 'queries/responseTests'
import { AITestTypeEnum } from '__generatedTypes__/globalTypes'
import {
  findAllAiTest,
  findAllAiTestVariables,
  findAllAiTest_findAllAiTest_items as AITest,
} from '__generatedTypes__/findAllAiTest'
import { updateAiTest, updateAiTestVariables } from '__generatedTypes__/updateAiTest'
import { bulkUpdateAiTest, bulkUpdateAiTestVariables } from '__generatedTypes__/bulkUpdateAiTest'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import * as classes from './styles'

type Props = {
  type: AITestTypeEnum
}

export const TestsList = ({ type }: Props) => {
  const apolloClient = useApolloClient()
  const { navigate, getLink } = useLink()
  const [submittingTestId, setSubmittingTestId] = useState<string | undefined>()
  const variables: findAllAiTestVariables = { query: { where: { type }, take: TAKE_ALL_ITEMS_COUNT } }
  const { data, loading } = useQuery<findAllAiTest, findAllAiTestVariables>(FIND_ALL_AI_TEST, {
    variables,
    fetchPolicy: 'cache-and-network',
  })
  const [updateTest] = useMutation<updateAiTest, updateAiTestVariables>(UPDATE_AI_TEST)
  const [bulkUpdateTests] = useMutation<bulkUpdateAiTest, bulkUpdateAiTestVariables>(BULK_UPDATE_AI_TEST)
  const tests = useMemo(() => orderBy(data?.findAllAiTest.items || [], (a) => a.order, 'asc'), [data?.findAllAiTest])

  const handleAddTestClick = useCallback(() => {
    navigate(cmsRoutes.RESPONSE_TESTS_CREATE, undefined, {
      searchParams: new URLSearchParams({
        type,
        defaultOrder: String(tests.length),
      }),
    })
  }, [type, tests.length])

  const handleSort = useCallback(async (testUpd: AITest[]) => {
    try {
      const newOrder = testUpd.map((reward, order) => ({ ...reward, order }))

      apolloClient.writeQuery<findAllAiTest, findAllAiTestVariables>({
        query: FIND_ALL_AI_TEST,
        variables,
        data: {
          findAllAiTest: {
            __typename: 'AiTestPaginationType',
            items: newOrder,
          },
        },
      })

      await bulkUpdateTests({
        variables: {
          input: newOrder.map(({ id, order }) => ({ id, order })),
        },
      })

      toast.success('Tests order updated successfully')
    } catch (error) {
      toast.error('Failed to update tests order')
    }
  }, [])

  const updateLiveInCache = useCallback(
    (test: AITest, isLive: boolean) => {
      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          __typename: test.__typename,
          id: test.id,
        }),
        fields: {
          isLive: () => isLive,
        },
      })
    },
    [apolloClient]
  )

  const handleIsLiveChange = useCallback(async (test: AITest, isLive: boolean) => {
    setSubmittingTestId(test.id)

    try {
      updateLiveInCache(test, isLive)

      await updateTest({
        variables: {
          input: {
            id: test.id,
            isLive,
          },
        },
      })
      toast.success('Test updated successfully')
    } catch (e) {
      updateLiveInCache(test, !isLive)

      const error = e as Error
      if (error.name === 'ApolloError') {
        toast.error(error.message)
      } else {
        toast.error('Failed to update Test')
      }
    }

    setSubmittingTestId(undefined)
  }, [])

  const getEditLink = useCallback(
    (testId: string) =>
      getLink(cmsRoutes.RESPONSE_TESTS_EDIT, {
        testId,
      }),
    []
  )

  if (loading && !tests.length) {
    return <PageLoader />
  }

  return (
    <div css={classes.tablesWrapper}>
      <Table<AITest>
        css={classes.table}
        columns={[
          {
            title: 'Title',
            renderCell: (test) => test.title,
          },
          {
            title: 'Live',
            renderCell: (test) => {
              const isLiveQuestionExists = test.questions.some((question) => question.isLive)

              if (!isLiveQuestionExists) {
                return (
                  <Tooltip
                    title="You cannot enable Live for Test with an empty list of Questions with Live enabled"
                    isNote
                    arrow
                  >
                    <Switch
                      checked={test.isLive}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e, isLive) => handleIsLiveChange(test, isLive)}
                      disabled
                    />
                  </Tooltip>
                )
              }

              return (
                <Switch
                  checked={test.isLive}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e, isLive) => handleIsLiveChange(test, isLive)}
                  disabled={test.id === submittingTestId}
                />
              )
            },
            flex: '0 1 50px',
          },
          {
            renderCell: (test) => (
              <Link to={getEditLink(test.id)}>
                <IconButton size="small" css={classes.link}>
                  <EditIcon />
                </IconButton>
              </Link>
            ),
            flex: '0 1 30px',
          },
        ]}
        items={tests}
        onSort={handleSort}
        isSortable
      />
      <TableButtons itemName={'Response Test'} onAddItem={handleAddTestClick} />
    </div>
  )
}
